import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.1 });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      alert('お問い合わせ内容は500文字以内でご入力ください。');
      return;
    }
    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d6mf0g46fojmejgajd8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-28 bg-[#F8F6F1]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Left Info */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? 'translateX(0)' : 'translateX(-48px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
          >
            <div className="flex items-center gap-3 mb-5 md:mb-6">
              <div className="w-8 h-px bg-[#B8960C]"></div>
              <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Contact</span>
            </div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4 md:mb-6 leading-tight"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              お問い合わせ
            </h2>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-8 md:mb-10">
              不動産に関するご相談・お問い合わせは、下記フォームまたはお電話にてお気軽にご連絡ください。専門スタッフが丁寧にご対応いたします。
            </p>

            <div className="space-y-5 md:space-y-6">
              {[
                { icon: 'ri-phone-line', label: 'お電話', value: '090-6002-2788', sub: '受付時間：平日 9:00〜18:00', href: 'tel:090-6002-2788' },
                { icon: 'ri-mail-line', label: 'メール', value: 'satomi102706@gmail.com', sub: '24時間受付', href: 'mailto:satomi102706@gmail.com' },
                { icon: 'ri-map-pin-2-line', label: '本社所在地', value: '千葉県市川市二俣2-14-18', sub: '', href: '' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 md:gap-4">
                  <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-[#B8960C]/10 rounded-md flex-shrink-0">
                    <i className={`${item.icon} text-base md:text-lg text-[#B8960C]`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-400 tracking-wider mb-0.5">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="font-medium text-[#1A1A1A] text-sm md:text-base hover:text-[#B8960C] transition-colors cursor-pointer break-words">
                        {item.value}
                      </a>
                    ) : (
                      <div className="font-medium text-[#1A1A1A] text-sm md:text-base break-words">{item.value}</div>
                    )}
                    {item.sub && <div className="text-xs text-gray-400 mt-0.5">{item.sub}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* SNS */}
            <div className="flex items-center gap-3 mt-8 md:mt-10">
              {['ri-twitter-x-line', 'ri-instagram-line', 'ri-facebook-fill', 'ri-youtube-line'].map((icon, i) => (
                <a
                  key={i}
                  href="#"
                  rel="nofollow"
                  className="w-10 h-10 flex items-center justify-center border border-gray-200 text-gray-400 hover:border-[#B8960C] hover:text-[#B8960C] transition-all cursor-pointer rounded-sm"
                >
                  <i className={`${icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className="bg-white p-6 md:p-10 rounded-sm shadow-lg"
            style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(48px)',
              transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
            }}
          >
            {submitted ? (
              <div className="text-center py-10 md:py-12">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-[#B8960C]/10 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-2xl md:text-3xl text-[#B8960C]"></i>
                </div>
                <h3
                  className="text-lg md:text-xl font-bold text-[#1A1A1A] mb-2"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  送信完了しました
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  お問い合わせありがとうございます。<br />
                  担当者より2営業日以内にご連絡いたします。
                </p>
              </div>
            ) : (
              <form
                id="contact-form-v2"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4 md:space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 tracking-wider mb-1.5">
                      お名前 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 px-4 py-3 text-sm text-[#1A1A1A] placeholder-gray-300 focus:outline-none focus:border-[#B8960C] transition-colors rounded-sm min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 tracking-wider mb-1.5">
                      会社名
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="株式会社〇〇"
                      className="w-full border border-gray-200 px-4 py-3 text-sm text-[#1A1A1A] placeholder-gray-300 focus:outline-none focus:border-[#B8960C] transition-colors rounded-sm min-h-[44px]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 tracking-wider mb-1.5">
                    メールアドレス <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="example@email.com"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-[#1A1A1A] placeholder-gray-300 focus:outline-none focus:border-[#B8960C] transition-colors rounded-sm min-h-[44px]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 tracking-wider mb-1.5">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="03-0000-0000"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-[#1A1A1A] placeholder-gray-300 focus:outline-none focus:border-[#B8960C] transition-colors rounded-sm min-h-[44px]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 tracking-wider mb-1.5">
                    工事種類 <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="construction_type"
                    required
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#B8960C] transition-colors rounded-sm bg-white cursor-pointer min-h-[44px]"
                  >
                    <option value="">工事種類を選択してください</option>
                    <option value="壁紙クロス工事">壁紙クロス工事</option>
                    <option value="ボード工事">ボード工事</option>
                    <option value="軽鉄下地LGS工事">軽鉄下地LGS工事</option>
                    <option value="耐震天井工事">耐震天井工事</option>
                    <option value="その他内装工事">その他内装工事</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 tracking-wider mb-1.5">
                    お問い合わせ種別
                  </label>
                  <select
                    name="inquiry_type"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#B8960C] transition-colors rounded-sm bg-white cursor-pointer min-h-[44px]"
                  >
                    <option value="">選択してください</option>
                    <option value="見積もり依頼">見積もり依頼</option>
                    <option value="施工相談">施工相談</option>
                    <option value="実績確認">実績確認</option>
                    <option value="採用について">採用について</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 tracking-wider mb-1.5">
                    お問い合わせ内容 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="ご相談内容をご記入ください（500文字以内）"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-[#1A1A1A] placeholder-gray-300 focus:outline-none focus:border-[#B8960C] transition-colors rounded-sm resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#B8960C] text-white py-4 text-sm font-medium tracking-widest hover:bg-[#9A7D0A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 min-h-[44px]"
                >
                  {loading ? '送信中...' : '送信する'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}